
import React from 'react';
import { LayoutDashboard, Upload, FileText, Settings, Database } from 'lucide-react';
import { ViewState } from '../types';

interface SidebarProps {
  currentView: ViewState;
  setView: (view: ViewState) => void;
  isOpen?: boolean;
  onClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ currentView, setView, isOpen = false, onClose }) => {
  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'upload', label: 'Data Import', icon: Upload },
    { id: 'data-editor', label: 'Data Manager', icon: Database },
    { id: 'reconciliation', label: 'Reconciliation', icon: FileText },
    { id: 'settings', label: 'Configuration', icon: Settings },
  ] as const;

  return (
    <>
      {/* Mobile Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 md:hidden backdrop-blur-sm"
          onClick={onClose}
        />
      )}

      {/* Sidebar Container */}
      <div className={`
        fixed inset-y-0 left-0 z-40 bg-slate-900 text-slate-300 h-full flex flex-col shadow-xl transition-all duration-300 ease-in-out
        md:shadow-none
        ${isOpen
          ? 'translate-x-0 w-64 md:static md:w-64'
          : '-translate-x-full w-64 md:translate-x-0 md:static md:w-0 md:overflow-hidden md:opacity-0'
        }
      `}>
        <div className="p-6 border-b border-slate-700 flex justify-between items-center min-w-[16rem]">
          <div>
            <h1 className="text-xl font-bold text-white flex items-center gap-2">
              <span className="bg-blue-600 w-8 h-8 rounded flex items-center justify-center text-xs">R</span>
              Reconciler Pro
            </h1>
            <p className="text-xs text-slate-500 mt-1">GSTR-2B Management</p>
          </div>
          {/* Mobile Close Button (Optional, clicking backdrop closes too) */}
          <button onClick={onClose} className="md:hidden text-slate-400 hover:text-white">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
          </button>
        </div>

        <nav className="flex-1 p-4 space-y-2 overflow-y-auto min-w-[16rem]">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = currentView === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setView(item.id as ViewState)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 whitespace-nowrap ${isActive
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/50'
                  : 'hover:bg-slate-800 hover:text-white'
                  }`}
              >
                <Icon size={20} />
                <span className="font-medium">{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-800">
          <div className="bg-slate-800 rounded p-3 text-xs text-slate-400">
            <p className="font-semibold text-slate-300">Pro Tip:</p>
            <p className="mt-1">Use Configuration to set Tax Tolerances before importing.</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
